import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	ArrowLeft,
	Edit2,
	Save,
	X,
	ChevronDown,
	ChevronUp,
	Check,
	XCircle,
	MapPin,
	Clock,
	HelpCircle
} from 'react-feather';
import { useCustomerStore } from './useCustomers';
import { useChatStore } from '@/pages/CustomerSupport/useChat';
import { FincluCustomer, CustomerProcessStatus, Chat } from '@/pages/CustomerSupport/types';
import Button from '@/stories/Library/Components/Button/Button';
import { getLanguageFromPath } from '@/utils/utils';
import { DEFAULT_LANGUAGE } from '@/constants/languages';
import { useTranslations } from 'use-intl';
import { formatPhoneNumber, formatDateTime } from '@/utils/utils';
import VehicleOfferingSection from './CustomerDetails/VehicleOfferingSection';
import QuestionnaireSection from './CustomerDetails/QuestionnaireSection';
import CustomerInformationSection from './CustomerDetails/CustomerInformationSection';
import SocialNetworkAnalysisSection from './CustomerDetails/SocialNetworkAnalysisSection';
import DefaultScoreAnalysisSection from './CustomerDetails/DefaultScoreAnalysisSection';
import CustomerDocumentsSection from './CustomerDetails/CustomerDocumentsSections';
import { FincluOperationStatus } from '@/types/FincluOperationStatus';
import { FincluStatusBadge } from './FincluStatusBadge';
import { UpdateFincluStatusModal } from './UpdateFincluStatusModal';
import { FincluDeliberationHistory } from './FincluDeliberationHistory';

const statusLabels: Record<CustomerProcessStatus, string> = {
	[CustomerProcessStatus.AGENT_CUSTOMER_SUPPORT]: 'StatusAgentCustomerSupport',
	[CustomerProcessStatus.SENT_APPROVED_MESSAGE]: 'StatusSentApprovedMessage',
	[CustomerProcessStatus.AGENT_FILLING_QUESTIONNARE]: 'StatusAgentFillingQuestionnare',
	[CustomerProcessStatus.AGENT_SUGGESTING_VEHICLE]: 'StatusAgentSuggestingVehicle',
	[CustomerProcessStatus.AGENT_GATHERING_DOCUMENTS]: 'StatusAgentGatheringDocuments',
	[CustomerProcessStatus.AGENT_GATHERING_SOCIAL_NETWORKS]: 'StatusAgentGatheringSocialNetworks',
	[CustomerProcessStatus.AGENT_ANALYZING_SOCIAL_NETWORKS]: 'StatusAgentAnalyzingSocialNetworks',
	[CustomerProcessStatus.AGENT_RISK_CALCULATION]: 'StatusAgentRiskCalculation'
};

const statusColors: Record<CustomerProcessStatus, string> = {
	[CustomerProcessStatus.AGENT_CUSTOMER_SUPPORT]: '#FFA500',
	[CustomerProcessStatus.SENT_APPROVED_MESSAGE]: '#4CAF50',
	[CustomerProcessStatus.AGENT_FILLING_QUESTIONNARE]: '#2196F3',
	[CustomerProcessStatus.AGENT_SUGGESTING_VEHICLE]: '#9C27B0',
	[CustomerProcessStatus.AGENT_GATHERING_DOCUMENTS]: '#FF9800',
	[CustomerProcessStatus.AGENT_GATHERING_SOCIAL_NETWORKS]: '#FF9800',
	[CustomerProcessStatus.AGENT_ANALYZING_SOCIAL_NETWORKS]: '#FF9800',
	[CustomerProcessStatus.AGENT_RISK_CALCULATION]: '#9C27B0'
};

const CustomerDetails: React.FC = () => {
	const t = useTranslations('SupportClients');
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const language = getLanguageFromPath(window.location.pathname) || DEFAULT_LANGUAGE;
	const {
		selectedCustomer,
		vehicleOffering,
		questionnaire,
		socialNetworkProfiles,
		analysisMetrics,
		documentRecord,
		loadCustomer,
		updateCustomer,
		isLoading
	} = useCustomerStore();
	const { chats, loadFilteredChats, setFilters } = useChatStore();

	const [isEditing, setIsEditing] = useState(false);
	const [editedCustomer, setEditedCustomer] = useState<Partial<FincluCustomer>>({});
	const [relatedChats, setRelatedChats] = useState<Chat[]>([]);
	const [isChatsExpanded, setIsChatsExpanded] = useState(true);
	const [areAllSectionsExpanded, setAreAllSectionsExpanded] = useState(true);
	const [statusModalOpen, setStatusModalOpen] = useState(false);
	const [preselectedStatus, setPreselectedStatus] = useState<FincluOperationStatus | null>(null);
	const [historyPanelOpen, setHistoryPanelOpen] = useState(false);

	useEffect(() => {
		if (id) {
			loadCustomer(id);
		}
	}, [id, loadCustomer]);

	useEffect(() => {
		if (selectedCustomer?.phone_number) {
			setFilters({ search: selectedCustomer.phone_number });
			loadFilteredChats(false);
		}
	}, [selectedCustomer?.phone_number, setFilters, loadFilteredChats]);

	useEffect(() => {
		if (selectedCustomer?.phone_number && chats.length > 0) {
			const filtered = chats.filter(
				(chat) => chat.customer_phone === selectedCustomer.phone_number
			);
			setRelatedChats(filtered);
		}
	}, [chats, selectedCustomer?.phone_number]);

	const handleEdit = () => {
		setIsEditing(true);
		setEditedCustomer(selectedCustomer || {});
	};

	const handleCancel = () => {
		setIsEditing(false);
		setEditedCustomer({});
	};

	const handleSave = async () => {
		if (id && editedCustomer) {
			const success = await updateCustomer(id, editedCustomer);
			if (success) {
				setIsEditing(false);
				setEditedCustomer({});
			}
		}
	};

	const handleFieldChange = (field: keyof FincluCustomer, value: any) => {
		setEditedCustomer((prev) => ({ ...prev, [field]: value }));
	};

	const handleToggleAllSections = () => {
		setAreAllSectionsExpanded(!areAllSectionsExpanded);
	};

	const handleStatusUpdateSuccess = () => {
		if (id) {
			loadCustomer(id);
		}
	};

	const handleOpenStatusModal = (status: FincluOperationStatus) => {
		setPreselectedStatus(status);
		setStatusModalOpen(true);
	};

	const handleCloseStatusModal = () => {
		setStatusModalOpen(false);
		setPreselectedStatus(null);
	};

	if (isLoading || !selectedCustomer) {
		return (
			<div className="p-6 text-center h-screen flex flex-col items-center justify-center">
				<div className="loader"></div>
			</div>
		);
	}

	const displayCustomer = isEditing ? editedCustomer : selectedCustomer;
	const currentStatus = selectedCustomer.finclu_operation_status as FincluOperationStatus;
	const isApproveDisabled =
		currentStatus === FincluOperationStatus.IN_PROGRESS ||
		currentStatus === FincluOperationStatus.APPROVED;
	const isRejectDisabled =
		currentStatus === FincluOperationStatus.IN_PROGRESS ||
		currentStatus === FincluOperationStatus.REJECTED;
	const customerFullName =
		`${selectedCustomer.names || ''} ${selectedCustomer.first_last_name || ''} ${selectedCustomer.second_last_name || ''}`.trim();
	const customerLocation =
		[selectedCustomer.city, selectedCustomer.state].filter(Boolean).join(', ') ||
		t('LocationNotAvailable');
	const riskScore = analysisMetrics?.default_score_analysis?.default_score;

	return (
		<div className="p-3 md:p-8 pb-36">
			<div className="flex items-center gap-4 mb-6">
				<button
					onClick={() => navigate(`/${language}/center/support-clients`)}
					className="flex items-center gap-2 p-2 border-none bg-transparent cursor-pointer text-blue-500"
				>
					<ArrowLeft size={20} />
				</button>
				<h1 className="text-2xl font-semibold flex-1">{t('CustomerDetails')}</h1>
				<Button
					label={areAllSectionsExpanded ? t('CollapseAllSections') : t('ExpandAllSections')}
					onClick={handleToggleAllSections}
					startIcon={areAllSectionsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					color="secondary"
					size="medium"
					outlined
				/>
				{!isEditing ? (
					<div className="hidden">
						<Button
							label={t('Edit')}
							onClick={handleEdit}
							startIcon={<Edit2 size={16} />}
							color="primary"
							size="medium"
						/>
					</div>
				) : (
					<div className="flex gap-2">
						<Button
							label={t('Cancel')}
							onClick={handleCancel}
							startIcon={<X size={16} />}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button
							label={t('Save')}
							onClick={handleSave}
							startIcon={<Save size={16} />}
							color="primary"
							size="medium"
						/>
					</div>
				)}
			</div>

			<CustomerInformationSection
				selectedCustomer={selectedCustomer}
				isEditing={isEditing}
				displayCustomer={displayCustomer}
				handleFieldChange={handleFieldChange}
				statusLabels={statusLabels}
				statusColors={statusColors}
				isExpanded={areAllSectionsExpanded}
			/>
			<QuestionnaireSection questionnaire={questionnaire} isExpanded={areAllSectionsExpanded} />
			<VehicleOfferingSection
				vehicleOffering={vehicleOffering}
				questionnaire={questionnaire}
				customerCreditPayment={selectedCustomer.credit_bureau_monthly_payment}
				organizationId={selectedCustomer.whatsapp_organization_id}
				isExpanded={areAllSectionsExpanded}
			/>
			<SocialNetworkAnalysisSection
				socialNetworkProfiles={socialNetworkProfiles}
				isExpanded={areAllSectionsExpanded}
				aiInterviewAnalysis={analysisMetrics?.ai_interview_analysis || null}
			/>
			<CustomerDocumentsSection
				documentRecord={documentRecord}
				isExpanded={areAllSectionsExpanded}
			/>
			<DefaultScoreAnalysisSection
				defaultScoreAnalysis={analysisMetrics?.default_score_analysis || null}
				analysisMetrics={analysisMetrics || null}
				isExpanded={areAllSectionsExpanded}
				customerId={selectedCustomer?.id}
				phoneNumber={selectedCustomer?.phone_number}
				organizationId={selectedCustomer?.whatsapp_organization_id}
				onRefetch={
					id
						? async () => {
								await loadCustomer(id);
							}
						: undefined
				}
			/>

			<div className="bg-white rounded-lg border border-gray-200 p-6 mb-24">
				<div
					className="flex items-center justify-between cursor-pointer mb-4"
					onClick={() =>
						setIsChatsExpanded(!areAllSectionsExpanded ? areAllSectionsExpanded : !isChatsExpanded)
					}
				>
					<h2 className="text-lg font-semibold">
						{t('RelatedChats')} ({relatedChats.length})
					</h2>
					{areAllSectionsExpanded && isChatsExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
				{areAllSectionsExpanded &&
					isChatsExpanded &&
					(relatedChats.length === 0 ? (
						<p className="text-gray-500 text-center py-6">{t('NoRelatedChats')}</p>
					) : (
						<div className="flex flex-col gap-3">
							{relatedChats.map((chat) => (
								<div
									key={chat.id}
									className="p-3 border border-gray-200 rounded-md cursor-pointer transition-colors hover:bg-gray-50"
									onClick={() => navigate(`/${language}/center/customer-support?chatId=${chat.id}`)}
								>
									<div className="flex justify-between items-center">
										<div>
											<p className="text-sm font-medium mb-1">
												{chat.customer_name || formatPhoneNumber(chat.customer_phone)}
											</p>
											<p className="text-xs text-gray-500">
												{chat.last_message_at
													? `Last message: ${formatDateTime(chat.last_message_at || '')}`
													: 'No messages yet'}
											</p>
										</div>
										<span className="px-3 py-1 rounded-xl bg-blue-50 text-blue-600 text-xs font-medium">
											{chat.status}
										</span>
									</div>
								</div>
							))}
						</div>
					))}
			</div>

			{selectedCustomer.finclu_operation_status && (
				<div className="fixed bottom-0 left-[230px] right-0 bg-white border-t border-gray-200 shadow-lg z-40">
					<div className="px-6 py-4">
						<div className="flex items-center justify-between gap-4">
							<div className="flex items-center gap-6">
								<span className="font-semibold text-gray-900 text-lg">
									{customerFullName || t('NameNotAvailable')}
								</span>
								<div className="flex items-center gap-2 text-gray-600">
									<MapPin size={16} />
									<span className="text-sm">{customerLocation}</span>
								</div>
								{riskScore !== undefined && riskScore !== null && (
									<div className="flex items-center gap-2">
										<span className="text-sm text-gray-500">{t('RiskScore')}:</span>
										<span
											className={`font-semibold ${riskScore * 100 > 50 ? 'text-red-600' : riskScore * 100 > 25 ? 'text-yellow-600' : 'text-green-600'}`}
										>
											{(riskScore * 100).toFixed(1)}%
										</span>
									</div>
								)}
								<div className="flex items-center gap-2">
									<FincluStatusBadge
										status={selectedCustomer.finclu_operation_status as FincluOperationStatus}
										size="md"
									/>
									{selectedCustomer.finclu_operation_deliberation_history &&
										selectedCustomer.finclu_operation_deliberation_history.length > 0 && (
											<button
												onClick={() => setHistoryPanelOpen(true)}
												className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
												title={t('DeliberationHistory')}
											>
												<Clock size={18} />
											</button>
										)}
								</div>
							</div>

							<div className="flex items-center gap-3">
								<Button
									label={t('AskForClarification')}
									onClick={() => handleOpenStatusModal(FincluOperationStatus.PENDING_CLARIFICATION)}
									startIcon={<HelpCircle size={18} />}
									color="warning"
									size="auto"
								/>
								<Button
									label={t('Reject')}
									onClick={() => handleOpenStatusModal(FincluOperationStatus.REJECTED)}
									startIcon={<XCircle size={18} />}
									color="error"
									size="small"
									disabled={isRejectDisabled}
									outlined
								/>
								<Button
									label={t('Approve')}
									onClick={() => handleOpenStatusModal(FincluOperationStatus.APPROVED)}
									startIcon={<Check size={18} />}
									color="success"
									size="small"
									disabled={isApproveDisabled}
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			{statusModalOpen && selectedCustomer.finclu_operation_status && (
				<UpdateFincluStatusModal
					customerId={selectedCustomer.id}
					currentStatus={selectedCustomer.finclu_operation_status as FincluOperationStatus}
					customerName={customerFullName}
					isOpen={statusModalOpen}
					onClose={handleCloseStatusModal}
					onSuccess={handleStatusUpdateSuccess}
					preselectedStatus={preselectedStatus}
				/>
			)}

			{/* Deliberation History Slide-out Panel */}
			{historyPanelOpen && (
				<>
					<div
						className="fixed inset-0 bg-black/30 z-40"
						onClick={() => setHistoryPanelOpen(false)}
					/>
					<div className="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-out">
						<div className="flex items-center justify-between p-4 border-b border-gray-200">
							<h2 className="text-lg font-semibold">{t('DeliberationHistory')}</h2>
							<button
								onClick={() => setHistoryPanelOpen(false)}
								className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
							>
								<X size={20} />
							</button>
						</div>
						<div className="p-4 overflow-y-auto h-[calc(100%-60px)]">
							{selectedCustomer.finclu_operation_deliberation_history &&
							selectedCustomer.finclu_operation_deliberation_history.length > 0 ? (
								<FincluDeliberationHistory
									history={selectedCustomer.finclu_operation_deliberation_history}
								/>
							) : (
								<p className="text-gray-500 text-center py-6">{t('NoDeliberationHistory')}</p>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default CustomerDetails;
